import {
  Checkbox,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Fab,
  Tab,
  Tabs
} from "@material-ui/core";
import useChunkedCsvDownload from "../hooks/useChunkedCsvDownload";
import { TabContext, TabPanel } from "@material-ui/lab";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import ConfirmationDialog from "./ConfirmationDialog";
import DatePicker from "./DatePicker";
import DateTimeRangePicker from "./DateTimeRangePicker";
import FormControl from "@material-ui/core/FormControl";
import React from "react";
import Typography from "@material-ui/core/Typography";
import _ from "lodash";
import api from "../api";
import dayjs from "dayjs";
import { isoString } from "../lithic/Moment";
import { makeStyles } from "@material-ui/core/styles";
import objectSupport from "dayjs/plugin/objectSupport";
import { useHistory } from "react-router";
import { useToastError } from "./FormError";
import { hasPms } from "../utils/partnerPms";
import useToggle from "../state/useToggle";

dayjs.extend(objectSupport);

function partnerIdFromActionLinkUrl(url) {
  const match = url?.match(/\/partners\/(\d+)\//);
  return match ? match[1] : null;
}

function EntrataRentersCsvDownloadButton({
  partnerId,
  includeMovedOut,
  enrollmentIds,
  disabled
}) {
  const { download, isDownloading, progress } = useChunkedCsvDownload();

  const handleDownload = () =>
    download({
      requestChunk: ({ offset, limit, skipHeaders }) =>
        api.downloadEntrataRentersCsv({
          partnerId,
          include_moved_out: includeMovedOut,
          enrollment_ids: enrollmentIds,
          offset,
          limit,
          skip_headers: skipHeaders
        }),
      filename: `entrata_renters_${partnerId}_${Date.now()}.csv`
    });

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleDownload}
        disabled={disabled || isDownloading}
      >
        {isDownloading ? "Downloading…" : "Download Entrata Renters CSV"}
      </Button>
      {isDownloading && progress && (
        <Box mt={1}>
          <LinearProgress
            variant="determinate"
            value={(progress.loaded / progress.total) * 100}
          />
          <Typography variant="caption" color="textSecondary">
            Loaded {progress.loaded} of {progress.total} batches…
          </Typography>
        </Box>
      )}
    </>
  );
}

export default function AdminActionLinks({
  links,
  partner,
  onActionSuccess,
  onActionError
}) {
  const classes = useStyles();
  const toggle = useToggle();
  const history = useHistory();
  const toastError = useToastError();
  onActionError = onActionError || toastError;

  const [activeLink, setActiveLink] = React.useState();
  const [customState, setCustomState] = React.useState({});
  const [tab, setTab] = React.useState("textField");
  const [file, setFile] = React.useState(null);

  if (_.isEmpty(links)) return null;

  const customControls = activeLink
    ? _.find(customControlDefs, ({ urlTemplate }) =>
        urlTemplate.test(activeLink.url)
      )
    : null;
  const fullCustomState = customControls && {
    ...customControls.defaultState(partner),
    ...customState
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== "text/csv") {
      toastError(new Error("Unsupported file type. Please upload a CSV file."));
      e.target.value = null;
      return;
    }
    setFile(file);
  };

  return (
    <Box mt={3} className={classes.container}>
      <Typography variant="h6" gutterBottom color="primary">
        Actions
      </Typography>
      {links
        .filter((link) => !link.hidden)
        .map((link) => (
          <div key={link.name} className={classes.buttonGroup}>
            <Button
              color="secondary"
              variant="contained"
              className={classes.action}
              onClick={() => {
                const controls = _.find(customControlDefs, ({ urlTemplate }) =>
                  urlTemplate.test(link.url)
                );

                const defaultState = controls
                  ? controls.defaultState(partner)
                  : {};
                const defaultPartnerId = link?.params?.defaultPartnerId;
                const availablePartners = link?.params?.availablePartners || [];
                const singlePartnerId =
                  availablePartners.length === 1
                    ? availablePartners[0].id
                    : null;

                setActiveLink(link);
                setCustomState({
                  ...defaultState,
                  ...(link.url?.includes("/relink_partner/") && defaultPartnerId
                    ? { partner_id: defaultPartnerId }
                    : {}),
                  ...(link.url?.includes("/relink_partner/") &&
                  !defaultPartnerId &&
                  singlePartnerId
                    ? { partner_id: singlePartnerId }
                    : {})
                });

                setTab("textField");
                setFile(null);
                toggle.toggle();
              }}
            >
              {link.name}
            </Button>
            <Typography>{link.help}</Typography>
          </div>
        ))}
      <ConfirmationDialog
        open={toggle.on}
        onCancel={toggle.turnOff}
        onConfirm={() => {
          let params = activeLink.params;
          if (
            customControls &&
            file &&
            file.type === "text/csv" &&
            tab === "csvUpload"
          ) {
            const formData = new FormData();
            formData.append("file", file);
            const controlParams = {
              ...params,
              ...customControls.toParams(fullCustomState)
            };
            delete controlParams.enrollment_ids;
            for (const key in controlParams)
              formData.append(key, controlParams[key]);

            params = formData;
            setFile(null);
          } else if (customControls && tab === "textField")
            params = { ...params, ...customControls.toParams(fullCustomState) };

          return api[activeLink.httpMethod](activeLink.url, params)
            .then(api.followRedirect(history))
            .then(onActionSuccess, onActionError)
            .finally(toggle.turnOff);
        }}
        text={!customControls && "Are you sure? " + _.get(activeLink, "help")}
        title={_.get(activeLink, "name")}
        cancelProps={{ children: "Go back" }}
        confirmProps={{
          children: "Yes",
          disabled: customControls?.isDisabled?.(
            fullCustomState,
            activeLink,
            file
          )
        }}
      >
        {customControls &&
          customControls.render({
            classes,
            customState: fullCustomState,
            setCustomState,
            tab,
            setTab,
            handleFileChange,
            file,
            activeLink,
            toastError,
            partner
          })}
      </ConfirmationDialog>
    </Box>
  );
}

/**
 * Support for custom controls in the admin action modals.
 * The urlTemplate should match the action URL
 * (we assume action urls are unique).
 * - defaultState returns a default state used for the controls;
 * - toParams takes the updated UI state and converts it into
 * data to be POSTed.
 * - render renders the components.
 */
const customControlDefs = [
  // Retry an invoice
  {
    urlTemplate: /\/v1\/invoices\/\d+\/retry/,
    defaultState: () => ({
      period: {
        start: dayjs().add(1, "hour"),
        end: dayjs().add(2, "hours")
      }
    }),
    toParams: function ({ period }) {
      return {
        periodBegin: isoString(period.start),
        periodEnd: isoString(period.end)
      };
    },
    render: function InvoiceRetry({ classes, customState, setCustomState }) {
      return (
        <div className={classes.modalInputs}>
          <DateTimeRangePicker
            value={customState.period}
            onChange={(period) => setCustomState({ period })}
            pickerProps={{ ampm: false }}
          />
        </div>
      );
    }
  },
  // Create a rental verification for a reporting account
  {
    urlTemplate:
      /\/v1\/reporting_account_enrollments\/\d+\/rental_verifications/,
    defaultState: () => ({
      month: "" + dayjs().month(),
      year: "" + dayjs().year(),
      closeTradeline: false
    }),
    toParams: function ({ month, year, closeTradeline }) {
      const p = { closeTradeline };

      p.month = Number(month) + 1;
      p.year = Number(year);

      return p;
    },
    render: function InvoiceRetry({ classes, customState, setCustomState }) {
      return (
        <div className={classes.modalInputs}>
          <FormControlLabel
            label="Close Tradeline"
            control={
              <Checkbox
                checked={customState.closeTradeline}
                onChange={(e) =>
                  setCustomState({
                    ...customState,
                    closeTradeline: e.target.checked
                  })
                }
              />
            }
          />
          <FormControl>
            <InputLabel>Month</InputLabel>
            <Select
              value={customState.month}
              onChange={(e) =>
                setCustomState({ ...customState, month: e.target.value })
              }
            >
              {_.range(0, 12).map((month) => (
                <MenuItem key={month} value={"" + month}>
                  {dayjs({ month }).format("MMMM")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Year</InputLabel>
            <Select
              value={customState.year}
              onChange={(e) =>
                setCustomState({ ...customState, year: e.target.value })
              }
            >
              {_.range(2000, dayjs().year() + 5).map((year) => (
                <MenuItem key={year} value={"" + year}>
                  {dayjs({ year }).format("YYYY")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      );
    }
  },
  // Custom control for rerunning individual reports
  {
    urlTemplate: /\/v1\/applicants\/\d+\/rerun_reports/,
    defaultState: () => ({
      reportType: "all"
    }),
    toParams: function ({ reportType }) {
      return {
        report_types: reportType === "all" ? undefined : [reportType]
      };
    },
    render: function ReportTypeSelector({
      classes,
      customState,
      setCustomState
    }) {
      return (
        <div className={classes.modalInputs}>
          <Typography className={classes.modalInput}>
            Are you sure? Re-run the reports for this application. This cost us
            $$$$$. Please only perform this action if you need to.
          </Typography>
          <FormControl className={classes.modalInput}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={customState.reportType}
              onChange={(e) => setCustomState({ reportType: e.target.value })}
            >
              <MenuItem value="all">All Reports</MenuItem>
              <MenuItem value="credit_report">Credit Report</MenuItem>
              <MenuItem value="criminal_report">Criminal Report</MenuItem>
              <MenuItem value="eviction_report">Eviction Report</MenuItem>
            </Select>
          </FormControl>
        </div>
      );
    }
  },
  // Re-verify rental verification
  {
    urlTemplate: /\/v1\/rental_verifications\/\d+\/reverify/,
    defaultState: () => ({
      reason: ""
    }),
    toParams: function ({ reason }) {
      return { reason };
    },
    render: function ReverifyRentalVerification({
      classes,
      customState,
      setCustomState
    }) {
      return (
        <div className={classes.modalInputs}>
          <Typography className={classes.modalInput} variant="body2">
            This will allow you to change the verification source or status for
            this rental verification.
          </Typography>
          <Typography
            className={classes.modalInput}
            variant="body2"
            color="error"
          >
            Warning: This will reset the rental verification to manual
            verification needed status and remove any existing verification
            sources.
          </Typography>
          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <TextField
              label="Reason for re-verification (optional)"
              multiline
              rows={3}
              value={customState.reason}
              variant="outlined"
              placeholder="e.g., PVS selected wrong transaction, customer provided proof of payment"
              onChange={(e) => setCustomState({ reason: e.target.value })}
            />
          </FormControl>
          <Typography
            className={classes.modalInput}
            variant="caption"
            color="textSecondary"
          >
            After confirming, the rental verification will be returned to manual
            verification status where you can select the correct source.
          </Typography>
        </div>
      );
    }
  },
  // Post rent reporting charges to Rent Manager and Entrata
  {
    urlTemplate: /\/v1\/partners\/\d+\/post_rent_reporting_charges/,
    defaultState: (partner = {}) => ({
      charge_type: partner.pmsChargeCode || "",
      amount: partner.pmsAmount?.cents ? partner.pmsAmount.cents / 100 : 0,
      date_string: dayjs().format("YYYY-MM-DD"),
      description: partner.pmsChargeName || "",
      enrollment_ids: "",
      include_moved_out: true
    }),
    toParams: function ({
      charge_type,
      amount,
      date_string,
      description,
      enrollment_ids,
      include_moved_out
    }) {
      const params = {
        charge_type,
        amount: parseFloat(amount.toString().replace(",", ".")),
        date_string,
        description,
        include_moved_out: Boolean(include_moved_out)
      };

      const trimmedEnrollmentIds = enrollment_ids?.trim() || "";

      if (trimmedEnrollmentIds)
        try {
          if (
            trimmedEnrollmentIds.startsWith("[") ||
            trimmedEnrollmentIds.startsWith("{")
          )
            params.enrollment_ids = JSON.parse(trimmedEnrollmentIds);
          else
            params.enrollment_ids = trimmedEnrollmentIds
              .split(",")
              .reduce((acc, id) => {
                const trimmedId = id.trim();
                if (trimmedId) acc.push(parseInt(trimmedId, 10));
                return acc;
              }, []);
        } catch (error) {
          params.enrollment_ids = enrollment_ids;
        }

      return params;
    },
    render: function PmsCharges({
      classes,
      customState,
      setCustomState,
      tab,
      setTab,
      handleFileChange,
      file,
      activeLink,
      toastError,
      partner
    }) {
      return (
        <div className={classes.modalInputs}>
          <Typography className={classes.modalInput}>
            Post rent reporting charges to resident ledgers in PMS
          </Typography>
          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <TextField
              focused
              required
              label="Charge Code/Type"
              value={customState.charge_type}
              variant="outlined"
              helperText="Charge code or type identifier in PMS"
              onChange={(e) =>
                setCustomState({ ...customState, charge_type: e.target.value })
              }
            />
          </FormControl>
          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <TextField
              required
              label="Amount"
              type="number"
              value={customState.amount}
              variant="outlined"
              inputProps={{ step: "0.01", min: "0" }}
              onChange={(e) =>
                setCustomState({ ...customState, amount: e.target.value })
              }
            />
          </FormControl>
          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <DatePicker
              label="Date"
              format="YYYY-MM-DD"
              value={customState.date_string}
              onChange={(date) =>
                setCustomState({
                  ...customState,
                  date_string: dayjs(date).format("YYYY-MM-DD")
                })
              }
            />
          </FormControl>

          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <TextField
              label="Description (Optional)"
              multiline
              minRows={2}
              placeholder={`for ${dayjs().format("MMMM YYYY")}`}
              value={customState.description}
              helperText="Appended to: BoomReport for customer {description} (defaults to 'for month year')"
              variant="outlined"
              onChange={(e) =>
                setCustomState({ ...customState, description: e.target.value })
              }
            />
          </FormControl>

          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={customState.include_moved_out}
                  onChange={(e) =>
                    setCustomState({
                      ...customState,
                      include_moved_out: e.target.checked
                    })
                  }
                />
              }
              label="Include renters with move out date"
            />
          </FormControl>

          {hasPms(partner, "entrata") && (
            <FormControl
              margin="normal"
              fullWidth
              className={classes.modalInput}
            >
              <Typography variant="subtitle2" gutterBottom>
                Download Renters Data
              </Typography>
              <EntrataRentersCsvDownloadButton
                partnerId={partnerIdFromActionLinkUrl(activeLink?.url)}
                includeMovedOut={customState.include_moved_out}
                enrollmentIds={customState.enrollment_ids}
                disabled={!partnerIdFromActionLinkUrl(activeLink?.url)}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: 4 }}
              >
                Download a CSV file with all eligible renters for review before
                posting charges.
              </Typography>
            </FormControl>
          )}

          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <Typography variant="subtitle1" gutterBottom>
              Enrollment IDs
            </Typography>

            <TabContext fullWidth value={tab}>
              <Tabs value={tab} onChange={(e, value) => setTab(value)}>
                <Tab key="textField" label="Textfield" value="textField" />
                <Tab
                  key="csvUpload"
                  label="Upload with CSV"
                  value="csvUpload"
                />
              </Tabs>
              <TabPanel value="textField">
                <TextField
                  label="Enrollment IDs"
                  multiline
                  value={customState.enrollment_ids}
                  variant="outlined"
                  placeholder="Enter comma-separated IDs"
                  helperText="Required. Enter enrollment IDs as a comma-separated list, or upload a CSV"
                  onChange={(e) =>
                    setCustomState({
                      ...customState,
                      enrollment_ids: e.target.value
                    })
                  }
                />
              </TabPanel>
              <TabPanel value="csvUpload">
                <label htmlFor="upload-EnrollmentIDs-file">
                  <TextField
                    type="file"
                    variant="outlined"
                    id="upload-EnrollmentIDs-file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />

                  <Fab
                    color="secondary"
                    size="small"
                    component="span"
                    aria-label="add"
                    variant="extended"
                  >
                    Upload CSV
                  </Fab>
                  <span> {file ? file.name : "No attached"} </span>
                </label>
              </TabPanel>
            </TabContext>
          </FormControl>
        </div>
      );
    },
    isDisabled: function (state, _, file) {
      const hasTextIds = state.enrollment_ids?.trim()?.length > 0;
      const hasFile = !!file;

      return !hasTextIds && !hasFile;
    }
  },
  // Enable Plaid for BoomScreen
  {
    urlTemplate: /\/v1\/partners\/\d+\/create_plaid_customer/,
    defaultState: (partner = {}) => {
      const missingFields = [];
      if (!partner.name) missingFields.push("Company Name");
      if (!partner.url) missingFields.push("Company URL");
      if (!partner.legalEntity?.address?.address1)
        missingFields.push("Street Address");
      if (!partner.legalEntity?.address?.city) missingFields.push("City");
      if (!partner.legalEntity?.address?.stateOrProvince)
        missingFields.push("State/Region");
      if (!partner.legalEntity?.address?.postalCode)
        missingFields.push("Postal Code");
      if (!partner.legalEntity?.address?.country)
        missingFields.push("Country Code");
      return {
        missingFields,
        customer_ip_address: "",
        customer_agreement_timestamp: dayjs()
      };
    },
    toParams: function ({ customer_ip_address, customer_agreement_timestamp }) {
      return {
        bank_addendum: {
          customer_ip_address,
          customer_agreement_timestamp: dayjs(
            customer_agreement_timestamp
          ).toISOString()
        }
      };
    },
    isDisabled: function ({
      missingFields,
      customer_ip_address,
      customer_agreement_timestamp
    }) {
      return (
        (missingFields && missingFields.length > 0) ||
        !customer_ip_address ||
        !customer_agreement_timestamp
      );
    },
    render: function EnablePlaidForBoomScreen({
      classes,
      customState,
      setCustomState
    }) {
      const {
        missingFields,
        customer_ip_address,
        customer_agreement_timestamp
      } = customState;
      if (missingFields.length > 0)
        return (
          <div>
            <Typography color="error" gutterBottom>
              The following required fields are missing:
            </Typography>
            <ul>
              {missingFields.map((field) => (
                <li key={field}>
                  <Typography color="error">{field}</Typography>
                </li>
              ))}
            </ul>
            <Typography variant="body2" style={{ marginTop: 16 }}>
              Please fill in the missing fields on the partner detail page
              before enabling Plaid for BoomScreen.
            </Typography>
          </div>
        );

      return (
        <div className={classes.modalInputs}>
          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <TextField
              required
              label="IP Address"
              value={customer_ip_address}
              variant="outlined"
              placeholder="e.g., 192.168.1.1"
              onChange={(e) =>
                setCustomState({
                  ...customState,
                  customer_ip_address: e.target.value
                })
              }
            />
          </FormControl>
          <FormControl margin="normal" fullWidth className={classes.modalInput}>
            <DatePicker
              label="Signature Date/Time"
              format="YYYY-MM-DD HH:mm"
              value={customer_agreement_timestamp}
              onChange={(date) =>
                setCustomState({
                  ...customState,
                  customer_agreement_timestamp: date
                })
              }
            />
          </FormControl>
        </div>
      );
    }
  },
  {
    urlTemplate: /\/v1\/reporting_account_enrollments\/\d+\/relink_partner/,
    defaultState: () => ({
      partner_id: null
    }),
    toParams: function ({ partner_id }) {
      return partner_id ? { partner_id } : {};
    },
    isDisabled: function ({ partner_id }, activeLink) {
      const availablePartners = activeLink?.params?.availablePartners || [];
      const defaultPartnerId = activeLink?.params?.defaultPartnerId;
      const hasImplicitPartner =
        defaultPartnerId ||
        (availablePartners.length === 1 && availablePartners[0]?.id);
      return (
        availablePartners.length === 0 || (!partner_id && !hasImplicitPartner)
      );
    },
    render: function RelinkPartner({
      classes,
      customState,
      setCustomState,
      activeLink
    }) {
      const availablePartners = activeLink?.params?.availablePartners || [];
      const defaultPartnerId = activeLink?.params?.defaultPartnerId;

      return (
        <div className={classes.modalInputs}>
          <Typography className={classes.modalInput} variant="body2">
            Re-link this customer to a partner. This restores the partner
            relationship without affecting enrollment status or subscription.
          </Typography>
          {availablePartners.length > 1 ? (
            <FormControl fullWidth className={classes.modalInput}>
              <InputLabel>Select Partner</InputLabel>
              <Select
                value={customState.partner_id ?? defaultPartnerId ?? ""}
                onChange={(e) =>
                  setCustomState({ ...customState, partner_id: e.target.value })
                }
              >
                {availablePartners.map((partner) => (
                  <MenuItem key={partner.id} value={partner.id}>
                    {partner.name}
                    {partner.id === defaultPartnerId ? " (Original)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : availablePartners.length === 1 ? (
            <Typography className={classes.modalInput} variant="body1">
              Partner: <strong>{availablePartners[0].name}</strong>
            </Typography>
          ) : (
            <Typography className={classes.modalInput} color="error">
              No partners available for re-linking.
            </Typography>
          )}
          <Typography
            className={classes.modalInput}
            variant="caption"
            color="textSecondary"
          >
            Note: The customer will remain in approved status. This action only
            restores the partner relationship without affecting subscription or
            billing.
          </Typography>
        </div>
      );
    }
  }
];

const useStyles = makeStyles((theme) => ({
  action: {
    marginRight: theme.spacing(2),
    boxSizing: "content-box"
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%"
  },
  modalInputs: {
    display: "flex",
    flexDirection: "column"
  },
  modalInput: {
    marginBottom: theme.spacing(2)
  }
}));
